import { rootApi } from '../../services/rootApi';
import { notesApi, pageModuleApi } from '../../utils/urls';

export const notesApiSlice = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchNotes: builder.query({
      query: () => ({
        url: notesApi(),
        method: 'POST',
        body: {
          method: 'findAll'
        },
      }),
      transformResponse: (response) => {
        const data = response.data;
        if (!data) return [];

        const byPage = Array.isArray(data.by_page) ? data.by_page : [];
        const byPageModule = Array.isArray(data.by_page_module) ? data.by_page_module : [];

        // Group by course
        const coursesMap = new Map();

        // Process by_page data
        byPage.forEach((courseData) => {
          const courseId = courseData.id || courseData.courseSlug;
          if (!coursesMap.has(courseId)) {
            coursesMap.set(courseId, {
              id: courseId,
              name: courseData.courseName || courseData.courseSlug,
              sections: []
            });
          }
          const course = coursesMap.get(courseId);

          if (courseData.sections && Array.isArray(courseData.sections)) {
            courseData.sections.forEach((sectionData) => {
              const existingSection = course.sections.find(s => s.id === sectionData.id);
              if (!existingSection) {
                course.sections.push({
                  id: sectionData.id,
                  name: sectionData.name,
                  pages: []
                });
              }

              const section = course.sections.find(s => s.id === sectionData.id);
              if (sectionData.pages && Array.isArray(sectionData.pages)) {
                sectionData.pages.forEach((pageData) => {
                  section.pages.push({
                    id: pageData.id,
                    name: pageData.name,
                    notes: pageData.notes || []
                  });
                });
              }
            });
          }
        });

        // Process by_page_module data
        byPageModule.forEach((moduleData) => {
          const courseId = moduleData.id || moduleData.courseSlug;
          if (!coursesMap.has(courseId)) {
            coursesMap.set(courseId, {
              id: courseId,
              name: moduleData.courseName || moduleData.courseSlug,
              sections: []
            });
          }
          const course = coursesMap.get(courseId);

          // Process sections and pages from the module data
          if (moduleData.sections && Array.isArray(moduleData.sections)) {
            moduleData.sections.forEach((sectionData) => {
              const existingSection = course.sections.find(s => s.id === sectionData.id);
              if (!existingSection) {
                course.sections.push({
                  id: sectionData.id,
                  name: sectionData.name,
                  pages: []
                });
              }
              
              const section = course.sections.find(s => s.id === sectionData.id);
              if (sectionData.pages && Array.isArray(sectionData.pages)) {
                sectionData.pages.forEach((pageData) => {
                  section.pages.push({
                    id: pageData.id,
                    name: pageData.name,
                    notes: pageData.notes || []
                  });
                });
              }
            });
          }
        });

        return Array.from(coursesMap.values());
      },
      providesTags: ['Notes'],
    }),
    saveNotes: builder.mutation({
      query: ({ pageModuleId, text }) => ({
        url: pageModuleApi(),
        method: 'POST',
        body: {
          data: {
            id: pageModuleId,
            note: {
              text,
              color: 'red'
            }
          },
          method: 'note'
        },
      }),
      invalidatesTags: ['Notes'],
    }),
    createNote: builder.mutation({
      query: ({ pageId, text }) => ({
        url: notesApi(),
        method: 'POST',
        body: {
          data: {
            id: pageId,
            text,
            color: 'red'
          },
          method: 'create'
        },
      }),
      invalidatesTags: (result, error, { pageId }) => ['Notes', { type: 'Page', id: pageId }],
    }),
    updateNote: builder.mutation({
      query: ({ noteId, pageId, text }) => ({
        url: notesApi(),
        method: 'POST',
        body: {
          data: {
            noteId,
            pageId,
            text,
          },
          method: 'update'
        },
      }),
      invalidatesTags: (result, error, { pageId }) => ['Notes', { type: 'Page', id: pageId }],
    }),
    deleteNote: builder.mutation({
      query: ({ pageId, noteId }) => ({
        url: notesApi(),
        method: 'POST',
        body: {
          data: {
            pageId,
            noteId,
          },
          method: 'removeNote'
        },
      }),
      invalidatesTags: (result, error, { pageId }) => ['Notes', { type: 'Page', id: pageId }],
    }),
  }),
});

export const {
  useFetchNotesQuery,
  useSaveNotesMutation,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
} = notesApiSlice;
